import { FC, useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { SystemColors } from 'melp-design/style';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Link,
  Loader,
  NothingFoundAlert,
  Panel,
  TextInput,
  Typography,
} from 'melp-design/components';
import { ROUTES } from 'config';
import { useConfirm } from 'store/confirm';
import {
  COLOR_BY_CONTENT_REVIEW_STATUS,
  ContentReviewField,
  contentReviewTypeLabel,
  useAdminContentReview,
  useApproveContentReview,
  useRejectContentReview,
} from 'store/admin-content-reviews';
import {
  getInlineDiff,
  InlineDiffSegment,
  segmentsForSide,
} from 'utils/diff/inline-diff';
import { usePredefinedToasts } from 'utils/Toast';
import { useRouteParams } from 'utils/useIdParam';
import moment from 'moment';

const DIFF_HIGHLIGHT_SX = {
  added: {
    display: 'inline',
    backgroundColor: '#abf2bc',
    borderRadius: '0.2em',
    boxDecorationBreak: 'clone',
    WebkitBoxDecorationBreak: 'clone',
    textDecoration: 'none',
  },
  removed: {
    display: 'inline',
    backgroundColor: 'rgba(255, 129, 130, 0.4)',
    borderRadius: '0.2em',
    boxDecorationBreak: 'clone',
    WebkitBoxDecorationBreak: 'clone',
    textDecoration: 'none',
  },
} as const;

const catalogPath = (entityType: string, liveEntityId: string) => {
  if (entityType === 'discount') {
    return ROUTES.admin.discounts.details.settings.replace(':id', liveEntityId);
  }
  if (entityType === 'company') {
    return ROUTES.admin.suppliers.details.replace(':id', liveEntityId);
  }
  if (entityType === 'service') {
    return ROUTES.admin.shopItems.details.settings.replace(':id', liveEntityId);
  }
  return undefined;
};

const prettyJson = (value: string | null) => {
  if (!value) {
    return '';
  }
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const fieldText = (field: ContentReviewField, side: 'live' | 'proposed') => {
  const value = side === 'live' ? field.live : field.proposed;
  if (field.kind === 'json') {
    return prettyJson(value);
  }
  return value ?? '';
};

const DiffSegment: FC<{ segment: InlineDiffSegment }> = ({ segment }) => {
  if (segment.type === 'equal') {
    return <span>{segment.value}</span>;
  }

  return (
    <Box
      component={segment.type === 'removed' ? 'del' : 'ins'}
      sx={DIFF_HIGHLIGHT_SX[segment.type]}
    >
      {segment.value}
    </Box>
  );
};

const DiffText: FC<{
  live: string;
  proposed: string;
  side: 'live' | 'proposed';
}> = ({ live, proposed, side }) => {
  const value = side === 'live' ? live : proposed;
  const segments = useMemo(
    () =>
      segmentsForSide(
        getInlineDiff(live, proposed),
        side === 'live' ? 'old' : 'new',
      ),
    [live, proposed, side],
  );

  if (!value) {
    return (
      <Typography color="textSecondary" component="span">
        —
      </Typography>
    );
  }

  return (
    <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {segments.map((segment, index) => (
        <DiffSegment key={`${segment.type}-${index}`} segment={segment} />
      ))}
    </Typography>
  );
};

const visibleFields = (
  fields: ContentReviewField[],
  onlyChanges: boolean,
): ContentReviewField[] =>
  fields.flatMap((field) => {
    if (field.kind === 'group') {
      const children = onlyChanges
        ? (field.children ?? []).filter((child) => child.changed)
        : field.children ?? [];
      if (onlyChanges && children.length === 0) {
        return [];
      }
      return [{ ...field, children }];
    }
    if (onlyChanges && !field.changed) {
      return [];
    }
    return [field];
  });

const FieldValue: FC<{
  field: ContentReviewField;
  side: 'live' | 'proposed';
}> = ({ field, side }) => {
  const value = side === 'live' ? field.live : field.proposed;

  if (field.kind === 'image') {
    return value ? (
      <Box
        component="img"
        src={value}
        alt=""
        sx={{
          maxWidth: '100%',
          maxHeight: 160,
          objectFit: 'contain',
          borderRadius: 1,
        }}
      />
    ) : (
      <Typography color="textSecondary">—</Typography>
    );
  }

  const live = fieldText(field, 'live');
  const proposed = fieldText(field, 'proposed');

  if (field.changed) {
    return <DiffText live={live} proposed={proposed} side={side} />;
  }

  const displayValue = side === 'live' ? live : proposed;

  return (
    <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {displayValue || '—'}
    </Typography>
  );
};

const FIELD_TABLE_COLUMNS =
  'minmax(140px, 0.26fr) minmax(0, 1fr) minmax(0, 1fr)';
const CHANGED_ROW_BG = 'rgba(243, 175, 0, 0.08)';

const FieldsTable: FC<{
  fields: ContentReviewField[];
  highlightChanges?: boolean;
}> = ({ fields, highlightChanges = false }) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        border: `1px solid ${SystemColors.grey[90]}`,
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: SystemColors.white,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: FIELD_TABLE_COLUMNS,
          gap: 2,
          px: 1.5,
          py: 1,
          bgcolor: SystemColors.grey[96],
          borderBottom: `1px solid ${SystemColors.grey[90]}`,
        }}
      >
        <Box />
        <Typography variant="p2" color="textSecondary">
          {t('contentReviews.live', { defaultValue: 'Live' })}
        </Typography>
        <Typography variant="p2" color="textSecondary">
          {t('contentReviews.proposed', { defaultValue: 'Proposed' })}
        </Typography>
      </Box>
      {fields.map((field) => (
        <Box
          key={field.key}
          sx={{
            display: 'grid',
            gridTemplateColumns: FIELD_TABLE_COLUMNS,
            gap: 2,
            px: 1.5,
            py: 1.25,
            alignItems: 'start',
            borderBottom: `1px solid ${SystemColors.grey[90]}`,
            bgcolor:
              highlightChanges && field.changed
                ? CHANGED_ROW_BG
                : SystemColors.white,
            '&:last-child': { borderBottom: 0 },
          }}
        >
          <Typography variant="h3" sx={{ m: 0, wordBreak: 'break-word' }}>
            {field.label}
          </Typography>
          <Box sx={{ minWidth: 0 }}>
            <FieldValue field={field} side="live" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <FieldValue field={field} side="proposed" />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const FieldGroup: FC<{
  field: ContentReviewField;
  onlyChanges: boolean;
}> = ({ field, onlyChanges }) => (
  <Panel
    title={field.label}
    collapsible
    isInitialExpanded={onlyChanges || field.changed}
    type="compact"
  >
    <FieldsTable
      fields={field.children ?? []}
      highlightChanges={!onlyChanges}
    />
  </Panel>
);

const FieldSections: FC<{
  fields: ContentReviewField[];
  onlyChanges: boolean;
}> = ({ fields, onlyChanges }) => {
  const sections: ContentReviewField[][] = [];
  let batch: ContentReviewField[] = [];

  for (const field of fields) {
    if (field.kind === 'group') {
      if (batch.length > 0) {
        sections.push(batch);
        batch = [];
      }
      sections.push([field]);
    } else {
      batch.push(field);
    }
  }
  if (batch.length > 0) {
    sections.push(batch);
  }

  return (
    <Stack gap={2}>
      {sections.map((section) => {
        const first = section[0];
        if (!first) {
          return null;
        }
        if (first.kind === 'group') {
          return (
            <FieldGroup
              key={first.key}
              field={first}
              onlyChanges={onlyChanges}
            />
          );
        }
        return (
          <FieldsTable
            key={section.map((item) => item.key).join('-')}
            fields={section}
            highlightChanges={!onlyChanges}
          />
        );
      })}
    </Stack>
  );
};

export const AdminContentReviewDetails: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const predefinedToasts = usePredefinedToasts();
  const { confirmation } = useConfirm();
  const { id: reviewId } = useRouteParams(ROUTES.admin.contentReviews.details);
  const [rejectReason, setRejectReason] = useState('');
  const [onlyChanges, setOnlyChanges] = useState(true);

  const { data: review, isLoading } = useAdminContentReview(reviewId);
  const { mutate: approve, isLoading: isApproving } = useApproveContentReview();
  const { mutate: reject, isLoading: isRejecting } = useRejectContentReview();

  const fields = useMemo(() => {
    if (!review) {
      return [];
    }
    return visibleFields(review.fields, onlyChanges);
  }, [onlyChanges, review]);

  if (isLoading) {
    return <Loader />;
  }
  if (!review) {
    return <NothingFoundAlert />;
  }

  const catalogUrl = catalogPath(review.entityType, review.liveEntityId);
  const isPending = review.status === 'pending';
  const type = contentReviewTypeLabel(review);

  return (
    <Stack gap={2}>
      <Panel
        title={review.entityName}
        subtitle={t(type.key, { defaultValue: type.defaultValue })}
        side={
          <Stack direction="row" gap={1} alignItems="center">
            <Typography color={COLOR_BY_CONTENT_REVIEW_STATUS[review.status]}>
              {t(`contentReviews.status.${review.status}`, {
                defaultValue: review.status,
              })}
            </Typography>
            {catalogUrl ? (
              <Link
                label={t('contentReviews.openLive', {
                  defaultValue: 'Open live',
                })}
                to={catalogUrl}
              />
            ) : null}
          </Stack>
        }
      >
        <Stack gap={1}>
          <Typography>
            {t('marketplaceItems.supplier')}: {review.supplier.name}
          </Typography>
          <Typography>
            {t('contentReviews.submittedAt', { defaultValue: 'Submitted' })}:{' '}
            {moment(review.submittedAt).format('l LT')}
            {review.submittedByName ? ` · ${review.submittedByName}` : ''}
          </Typography>
          {review.reviewedAt ? (
            <Typography>
              {t('contentReviews.reviewedAt', { defaultValue: 'Reviewed' })}:{' '}
              {moment(review.reviewedAt).format('l LT')}
              {review.reviewedByName ? ` · ${review.reviewedByName}` : ''}
            </Typography>
          ) : null}
          {review.rejectReason ? (
            <Typography>
              {t('contentReviews.rejectReason', {
                defaultValue: 'Reject reason',
              })}
              : {review.rejectReason}
            </Typography>
          ) : null}
        </Stack>
      </Panel>

      {review.hasConflict ? (
        <Alert
          severity="warning"
          title={t('contentReviews.conflictTitle', {
            defaultValue: 'Live content changed after this copy was taken',
          })}
        >
          {t('contentReviews.conflictBody', {
            defaultValue:
              'You can still confirm and overwrite the live version, or reject the copy.',
          })}
        </Alert>
      ) : null}

      {isPending ? (
        <>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="neutral-outline"
              label={
                onlyChanges
                  ? t('contentReviews.showAllFields', {
                      defaultValue: 'Show all fields',
                    })
                  : t('contentReviews.showChangesOnly', {
                      defaultValue: 'Show changes only',
                    })
              }
              onClick={() => setOnlyChanges((current) => !current)}
            />
          </Stack>

          {fields.length === 0 ? (
            <Alert>
              {t('contentReviews.noChanges', {
                defaultValue: 'No field differences.',
              })}
            </Alert>
          ) : (
            <FieldSections
              key={String(onlyChanges)}
              fields={fields}
              onlyChanges={onlyChanges}
            />
          )}
        </>
      ) : null}

      {isPending ? (
        <Panel
          title={t('contentReviews.decision', { defaultValue: 'Decision' })}
        >
          <Stack gap={2}>
            <TextInput
              label={t('contentReviews.rejectReason', {
                defaultValue: 'Reject reason',
              })}
              value={rejectReason}
              onChange={setRejectReason}
              rows={3}
            />
            <Stack direction="row" gap={1} justifyContent="flex-end">
              <Button
                variant="danger-outline"
                label={t('contentReviews.reject', { defaultValue: 'Reject' })}
                loading={isRejecting}
                onClick={async () => {
                  const { confirmed } = await confirmation(
                    t('contentReviews.rejectConfirm', {
                      defaultValue:
                        'The proposed copy will be discarded. The live version stays unchanged.',
                    }),
                    t('contentReviews.reject', { defaultValue: 'Reject' }),
                    'danger',
                  );
                  if (!confirmed) {
                    return;
                  }
                  reject(
                    { reviewId, reason: rejectReason },
                    {
                      onSuccess: () => {
                        predefinedToasts.success.updated();
                        history.push(ROUTES.admin.contentReviews.list);
                      },
                    },
                  );
                }}
              />
              <Button
                label={t('contentReviews.confirm', { defaultValue: 'Confirm' })}
                loading={isApproving}
                onClick={async () => {
                  const { confirmed } = await confirmation(
                    t('contentReviews.confirmPrompt', {
                      defaultValue:
                        'Apply the proposed copy onto the live version?',
                    }),
                  );
                  if (!confirmed) {
                    return;
                  }
                  approve(
                    { reviewId },
                    {
                      onSuccess: () => {
                        predefinedToasts.success.updated();
                        history.push(ROUTES.admin.contentReviews.list);
                      },
                    },
                  );
                }}
              />
            </Stack>
          </Stack>
        </Panel>
      ) : null}
    </Stack>
  );
};
